"use client";

import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

interface MenuItem {
  id: number;
  title: string;
  slug: string;
  url?: string;
  priority: number;
  status: boolean;
  icon?: string;
  text_color?: string;
  is_button: boolean;
  button_color?: string;
  children?: MenuItem[];
}

interface RouteItem {
  id: number;
  route: string;
  label: string;
  type?: string;
  nestedRoutes?: {
    id: number;
    label: string;
    route: string;
    textColor?: string;
  }[];
  isButton?: boolean;
  buttonColor?: string;
  textColor?: string;
}

const NavBar2 = ({ data }: { data: any }) => {
  const { data: sectionData, isPreview = false } = data;
  const pathname = usePathname();
  const dispatch = useDispatch();

  if (!sectionData) {
    return null;
  }

  const {
    background_color,
    menuItems = [],
    logo_icon,
    logo_icon_alt,
    program_name,
    program_name_text_color,
    border_color,
    horizontal_stripe_color,
    text_color,
    hamburger_icon,
    mobile_cancel_icon,
    mobile_cancel_icon_alt,
    mobile_logo_icon,
    mobile_logo_icon_alt,
    show_hamburger,
  } = sectionData;

  // Hamburger visibility. Defaults TRUE — a missing or NULL value must keep
  // the hamburger, so only an explicit editor opt-out ever hides it.
  const showHamburger = !(
    show_hamburger === false ||
    show_hamburger === 0 ||
    show_hamburger === "0"
  );

  // Transform menuItems from backend to routes format
  const routes: RouteItem[] = useMemo(() => {
    if (!menuItems || menuItems.length === 0) {
      return [];
    }

    const activeItems = menuItems
      .filter((item: MenuItem) => item.status)
      .sort((a: MenuItem, b: MenuItem) => a.priority - b.priority);

    return activeItems.map((item: MenuItem) => {
      const routePath =
        item.url && item.url.trim() !== "" ? item.url : `/${item.slug}`;
      const route: RouteItem = {
        id: item.id,
        route: routePath,
        label: item.title,
        type: "header",
        isButton: Boolean(item.is_button),
        buttonColor: item.button_color,
        textColor: item.text_color,
      };

      if (item.children && item.children.length > 0) {
        const activeChildren = item.children
          .filter((child: MenuItem) => child.status)
          .sort((a: MenuItem, b: MenuItem) => a.priority - b.priority);

        route.nestedRoutes = activeChildren.map((child: MenuItem) => ({
          id: child.id,
          label: child.title,
          route:
            child.url && child.url.trim() !== "" ? child.url : `/${child.slug}`,
          textColor: child.text_color,
        }));
      }

      return route;
    });
  }, [menuItems]);

  const menuRoutes = routes.filter((route) => !route.isButton);
  const buttonRoutes = routes.filter((route) => route.isButton);

  const activePath = menuRoutes.find(
    (route) => route.route === pathname,
  )?.route;

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDesktopDropdown, setOpenDesktopDropdown] = useState<number | null>(
    null,
  );
  const [openMobileAccordion, setOpenMobileAccordion] = useState<number | null>(
    null,
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const [navBottom, setNavBottom] = useState(52);

  useEffect(() => {
    if (isMobileMenuOpen && navRef.current) {
      setNavBottom(navRef.current.getBoundingClientRect().bottom);
    }
  }, [isMobileMenuOpen]);

  // Close desktop dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpenDesktopDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (routes.length === 0 && !logo_icon && !program_name) {
    return null;
  }

  return (
    <>
      <nav
        ref={navRef}
        className={cn(
          "flex items-center justify-between",
          "px-[24px] py-[10px] sm:px-[40px] sm:py-[6px] lg:px-[60px] xl:px-[120px]",
          "relative w-full z-50",
        )}
        style={{
          backgroundColor: background_color || "#000000",
          borderBottom: `0.6px solid ${border_color || "rgba(255,255,255,0.2)"}`,
        }}
      >
        {/* Left section: Logo + Divider + Program Title */}
        <div className="flex items-center gap-[8px] sm:gap-[15px]">
          {/* Logo */}
          {logo_icon && (
            <div className="w-[62px] h-[32px] sm:w-[88px] sm:h-[45px] relative overflow-hidden shrink-0">
              <Image
                src={logo_icon}
                alt={logo_icon_alt || "Logo"}
                fill
                className="object-contain"
              />
            </div>
          )}

          {/* Vertical Divider */}
          {logo_icon && program_name && (
            <div
              className="h-[30px] w-[0.6px] shrink-0"
              style={{
                backgroundColor:
                  horizontal_stripe_color || "rgba(255,255,255,0.3)",
              }}
            />
          )}

          {/* Program Name */}
          {program_name && (
            <p
              className="text-[11px] font-medium w-[134px] tracking-[0.22px] leading-normal sm:text-[12px] sm:tracking-[0.24px]"
              style={{ color: program_name_text_color || "#56e2f5" }}
            >
              {program_name}
            </p>
          )}
        </div>

        {/* Mobile: Hamburger / Close Button */}
        {showHamburger && (
        <button
          className="sm:hidden flex items-center justify-center size-[22px] shrink-0"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          disabled={!hamburger_icon}
        >
          {isMobileMenuOpen ? (
            mobile_cancel_icon ? (
              <Image
                src={mobile_cancel_icon}
                alt={mobile_cancel_icon_alt || "Close"}
                width={22}
                height={22}
              />
            ) : (
              <svg
                width="22"
                height="22"
                viewBox="0 0 22 22"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6 6L16 16M16 6L6 16"
                  stroke={text_color || "#ffffff"}
                  strokeWidth="1.65"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )
          ) : hamburger_icon ? (
            <Image src={hamburger_icon} alt="Menu" width={22} height={22} />
          ) : (
            null
          )}
        </button>
        )}

        {/* Desktop: Navigation Links */}
        <div
          ref={dropdownRef}
          className="hidden sm:flex items-center justify-between sm:w-[600px]"
        >
          {menuRoutes.map((route) =>
            route.nestedRoutes && route.nestedRoutes.length > 0 ? (
              <div key={route.id} className="relative">
                <button
                  onClick={() =>
                    setOpenDesktopDropdown(
                      openDesktopDropdown === route.id ? null : route.id,
                    )
                  }
                  className={cn(
                    "uppercase sm:text-[11px] sm:font-semibold sm:tracking-[0.11px] sm:leading-normal lg:text-[14px] lg:tracking-[0.14px] transition-opacity hover:opacity-80 flex items-center gap-[6px]",
                    {
                      "opacity-80": activePath === route.route,
                    },
                  )}
                  style={{
                    color: route.textColor || text_color || "#ffffff",
                  }}
                >
                  {route.label}
                  <svg
                    width="10"
                    height="6"
                    viewBox="0 0 10 6"
                    fill="none"
                    className={cn(
                      "transition-transform duration-200",
                      openDesktopDropdown === route.id && "rotate-180",
                    )}
                  >
                    <path
                      d="M1 1L5 5L9 1"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                {openDesktopDropdown === route.id && (
                  <div
                    className="absolute top-full left-0 mt-[10px] min-w-[180px] rounded-[4px] py-[8px] z-50 shadow-lg"
                    style={{
                      backgroundColor: background_color || "#000000",
                      border: `0.6px solid ${border_color || "rgba(255,255,255,0.2)"}`,
                    }}
                  >
                    {route.nestedRoutes.map((nested) => (
                      <Link
                        key={nested.id}
                        href={nested.route}
                        onClick={() => setOpenDesktopDropdown(null)}
                        className="block px-[16px] py-[8px] text-[13px] font-medium tracking-[0.13px] uppercase transition-opacity hover:opacity-80"
                        style={{
                          color:
                            nested.textColor ||
                            route.textColor ||
                            text_color ||
                            "#ffffff",
                        }}
                      >
                        {nested.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={route.id}
                href={route.route}
                className={cn(
                  "uppercase sm:text-[11px] sm:font-semibold sm:tracking-[0.11px] sm:leading-normal lg:text-[14px] lg:tracking-[0.14px] transition-opacity hover:opacity-80",
                  {
                    "opacity-80": activePath === route.route,
                  },
                )}
                style={{
                  color: route.textColor || text_color || "#ffffff",
                }}
              >
                {route.label}
              </Link>
            ),
          )}
          {buttonRoutes.map((route) => (
            <button
              key={route.id}
              onClick={() =>
                dispatch(
                  handleOpenLeaddrop({
                    variant: LEAD_DROP_VARIANTS.REGISTER,
                  }),
                )
              }
              className="uppercase sm:text-[11px] sm:font-semibold sm:tracking-[0.11px] sm:leading-normal lg:text-[14px] lg:tracking-[0.14px] rounded-[4px] px-[16px] py-[8px] transition-opacity hover:opacity-90 shrink-0 cursor-pointer"
              style={{
                backgroundColor: route.buttonColor || "#ffffff",
                color: route.textColor || text_color || "#000000",
              }}
            >
              {route.label}
            </button>
          ))}
        </div>
      </nav>

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <div
          className={cn(
            "sm:hidden flex flex-col gap-[24px] items-start px-[24px] py-[40px] w-full z-40",
            isPreview ? "relative" : "fixed left-0 right-0",
          )}
          style={{
            backgroundColor: background_color || "#000000",
            opacity: 0.7,
            borderBottom: "1px solid rgba(255,255,255,0.8)",
            ...(isPreview ? {} : { top: `${navBottom}px` }),
          }}
        >
          {menuRoutes.map((route) =>
            route.nestedRoutes && route.nestedRoutes.length > 0 ? (
              <div key={route.id} className="w-full">
                <button
                  onClick={() =>
                    setOpenMobileAccordion(
                      openMobileAccordion === route.id ? null : route.id,
                    )
                  }
                  className={cn(
                    "uppercase text-[16px] font-semibold tracking-[0.16px] leading-normal transition-opacity hover:opacity-80 flex items-center gap-[8px] w-full",
                    {
                      "opacity-80": activePath === route.route,
                    },
                  )}
                  style={{
                    color: route.textColor || text_color || "#ffffff",
                  }}
                >
                  {route.label}
                  <svg
                    width="10"
                    height="6"
                    viewBox="0 0 10 6"
                    fill="none"
                    className={cn(
                      "transition-transform duration-200",
                      openMobileAccordion === route.id && "rotate-180",
                    )}
                  >
                    <path
                      d="M1 1L5 5L9 1"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                {openMobileAccordion === route.id && (
                  <div className="flex flex-col gap-[16px] mt-[16px] pl-[16px]">
                    {route.nestedRoutes.map((nested) => (
                      <Link
                        key={nested.id}
                        href={nested.route}
                        onClick={() => {
                          setOpenMobileAccordion(null);
                          setIsMobileMenuOpen(false);
                        }}
                        className="text-[14px] font-medium tracking-[0.14px] leading-normal uppercase transition-opacity hover:opacity-80"
                        style={{
                          color:
                            nested.textColor ||
                            route.textColor ||
                            text_color ||
                            "#ffffff",
                        }}
                      >
                        {nested.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={route.id}
                href={route.route}
                onClick={() => setIsMobileMenuOpen(false)}
                className={cn(
                  "uppercase text-[16px] font-semibold tracking-[0.16px] leading-normal transition-opacity hover:opacity-80",
                  {
                    "opacity-80": activePath === route.route,
                  },
                )}
                style={{
                  color: route.textColor || text_color || "#ffffff",
                }}
              >
                {route.label}
              </Link>
            ),
          )}
          {buttonRoutes.map((route) => (
            <button
              key={route.id}
              onClick={() => {
                setIsMobileMenuOpen(false);
                dispatch(
                  handleOpenLeaddrop({
                    variant: LEAD_DROP_VARIANTS.REGISTER,
                  }),
                );
              }}
              className="uppercase text-[16px] font-semibold tracking-[0.16px] leading-normal rounded-[4px] px-[20px] py-[10px] text-center transition-opacity hover:opacity-90 cursor-pointer"
              style={{
                backgroundColor: route.buttonColor || "#ffffff",
                color: route.textColor || text_color || "#000000",
              }}
            >
              {route.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
};

export default NavBar2;
